import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {Store} from '@ngrx/store';
import {CartItem} from 'src/app/data/model/cart-item';
import * as cartActions from 'src/app/common/shopping-cart/cart.actions';
import * as cartSelectors from 'src/app/common/shopping-cart/cart.selectors';
import {OnDestroySubject} from 'src/app/data/model';
import {Observable} from 'rxjs';
import {CommerceInitializationService} from '../../../../commerce-initialization.service';
import {
  AddOnErrorEvent,
  AuthenticateEvent,
  CartChangeEvent,
} from '../../../event-emitter/event.interface';
import {CommerceEventEmitter} from '../../../event-emitter/commerce-event-emitter';
import {SeFeThemesLoader} from 'se-fe-themes';

@Component({
  selector: 'app-cart-companion',
  templateUrl: './cart-companion.component.html',
  styleUrls: ['./cart-companion.component.scss'],
  host: {class: 'app-cart-companion'},
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CartCompanionComponent
  extends OnDestroySubject
  implements OnInit, OnDestroy, AfterViewInit, CommerceEventEmitter
{
  /**
   * Theme base URL for SeFeThemesLoader – same as CommerceComponent.
   */
  private static readonly THEME_URL =
    'https://se-fe-angular-services.ui.sportngin.com';

  @Output() cartLoad = new EventEmitter<CartChangeEvent>();
  @Output() cartUpdate = new EventEmitter<CartChangeEvent>();
  @Output() addOnError = new EventEmitter<AddOnErrorEvent>();
  @Output() authenticate = new EventEmitter<AuthenticateEvent>();

  cartItems$: Observable<CartItem[]>;
  private isInitializing = false;
  private initializeRunId = 0;
  private destroyed = false;

  constructor(
    private store: Store,
    private initializationService: CommerceInitializationService,
  ) {
    super();
    this.initializationService.enableAddressCollection = false;
    this.initializationService.enableCartSlideOut = false;
  }

  @Input() set context(value: string) {
    this.initializationService.context = value;
  }

  ngOnInit(): void {
    this.destroyed = false;
    void this.initialize().catch(() => undefined);
    this.store.dispatch(cartActions.setEnableCartSlideOut({enabled: false}));
    this.cartItems$ = this.store.select(
      cartSelectors.selectCartItemsForDisplay,
    );
  }

  ngAfterViewInit(): void {
    this.loadTheme();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.initializeRunId++;
    super.ngOnDestroy();
  }

  private async initialize(): Promise<void> {
    if (this.isInitializing) {
      return;
    }

    this.isInitializing = true;
    const runId = ++this.initializeRunId;
    try {
      await this.initializationService.initialize(this);
      if (this.destroyed || runId !== this.initializeRunId) {
        return;
      }
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Loads the SeFe theme globally so that all --se-* design tokens and
   * interactive CSS classes are available for SeFe child components.
   * Unlike CommerceComponent (which loads into its shadow root), the
   * Cart Companion has no shadow DOM, so the theme loads into the
   * document head and applies to the page.
   */
  private loadTheme(): void {
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: CartCompanionComponent.THEME_URL,
    }).catch(() => {
      // Theme load failure is non-fatal; companion still works with CSS fallbacks
    });
  }
}
